/**
 * Shared, DB-handle-agnostic PowerSync ↔ Supabase connector logic.
 *
 * The main-process connector (src/main/lib/powersync/supabaseConnector.ts,
 * @powersync/node) and the renderer/web-fallback connector
 * (src/renderer/lib/powersync/supabaseConnector.ts, @powersync/web) each
 * implement uploadData() against a different PowerSync SDK, so the actual
 * DB/Supabase-client plumbing must stay in each file. Everything that is
 * pure data — error classification, which tables to clean up, which
 * columns/constraints need special upsert handling — lives here once so the
 * two connectors cannot silently drift out of sync with each other again.
 */

// Postgres error codes that are unrecoverable — will never succeed on retry,
// no matter how long we wait. Discard the transaction instead of blocking
// the upload queue indefinitely. Deliberately narrow: 22xxx/23xxx/42501 are
// genuinely deterministic (a type mismatch, constraint violation, or RLS
// rejection can never change without the row itself changing). A plain
// `RAISE EXCEPTION` (SQLSTATE P0001, e.g. "Invalid table_id" or "No open
// accounting period covers this date") is deliberately NOT in this list —
// unlike the classes above, these are often blocked on an external condition
// (an admin opening an accounting period, a device being re-registered) that
// can resolve on its own, so they go through the grace-period path instead
// of being discarded on the very first failure.
export const FATAL_RESPONSE_CODES = [
  /^22...$/, // Class 22 — Data Exception (type mismatch, etc.)
  /^23...$/, // Class 23 — Integrity Constraint Violation (NOT NULL, FK, UNIQUE)
  /^42501$/, // INSUFFICIENT PRIVILEGE — typically an RLS violation
];

// Grace period for errors that reached the server but aren't recognized as
// fatal (see FATAL_RESPONSE_CODES above) — gives a real chance for a
// resolvable external condition to clear (an admin opening an accounting
// period, a temporary server-side issue) before giving up. Still bounded, so
// a genuinely permanent-but-unclassified error is eventually cleaned up
// rather than retried forever.
export const UPLOAD_RETRY_GRACE_PERIOD_MS = 30 * 60 * 1000; // 30 minutes

// Tables whose Postgres PK column is 'outlet_id', not 'id'.
// PowerSync tracks rows by `id` internally; uploads must use 'outlet_id'.
export const OUTLET_ID_PK_TABLES = new Set([
  'outlet_order_counters',
  'outlet_kot_counters',
  'outlet_ebm_invoice_counters',
]);

// Tables that enforce a unique constraint on a business-key column other than
// the primary key `id`. PowerSync always upserts on `id`, but these tables
// need a different conflict column to match server-side upsert semantics:
//
//   discounts: UNIQUE (order_id) — one discount per order.
//   The server-side add_order_discount RPC uses ON CONFLICT (order_id),
//   so we must mirror that here to avoid duplicate-key violations when
//   uploading offline-created discounts.
export const UPSERT_ON_ORDER_ID_TABLES = new Set(['discounts']);

// Tables that grant INSERT but deliberately withhold UPDATE — ledger rows
// meant to be append-only, mutable only via a SECURITY DEFINER RPC. Any
// code path that locally mirrors an RPC's own INSERT (a "cache-seed" write
// — see localSeedRpcSettlement in localOrderOps.ts) queues a duplicate PUT
// for the same id once PowerSync uploads it. Because the row already
// exists server-side, the generic upsert would otherwise resolve via
// UPDATE, which has no grant/policy to satisfy — 42501. Upserting with
// ignoreDuplicates: true turns that into INSERT ... ON CONFLICT (id) DO
// NOTHING, which never touches the missing UPDATE path. Safe ONLY when the
// local mirror write is a verbatim copy of what the RPC already wrote
// server-side (true today for order_settlements — see directSettleOrder
// etc. in orderService.ts, which return the RPC's own row and pass it
// straight through). Before adding a table here, confirm no local write to
// it can ever legitimately differ from the server row on conflict — DO
// NOTHING would silently drop that difference instead of erroring.
export const IGNORE_DUPLICATES_ON_ID_TABLES = new Set<string>([
  'order_settlements',
]);

// Columns declared GENERATED ALWAYS AS … STORED in Postgres must not be sent
// in INSERT / UPDATE payloads — Supabase rejects them.
export const GENERATED_COLUMNS: Record<string, string[]> = {
  order_items: ['amount'],
};

// Every table with an order_id FK that must be cleaned up when an order is
// discarded — otherwise a row still queued for upload (e.g. a settlement
// recorded before this order was discarded) fails upload with a 23503 FK
// violation once its parent order is gone, which matches FATAL_RESPONSE_CODES
// and gets silently discarded on its own, potentially dropping a real
// payment record with no specific error surfaced. Order matters: children
// before the parent row.
export const ORDER_CHILD_TABLE_CLEANUP_ORDER = [
  'order_settlements',
  'kitchen_tickets',
  'item_cancellations',
  'order_items',
  'discounts',
];

// Pure connectivity failures — the request never reached the server at all
// (no PostgREST/Postgres response, just a raw fetch/DNS/socket error). This
// is the expected, normal shape of "the device is offline" and must never
// count against any discard logic — PowerSync's own retry loop (a flat
// 5-second interval) already handles this indefinitely, for as long as the
// device stays offline. Recognized by message pattern since these arrive as
// plain JS errors with no Postgrest error code at all.
export function isNetworkConnectivityError(message?: string | null): boolean {
  if (!message) return false;
  return /fetch failed|failed to fetch|network|enotfound|econnrefused|econnreset|etimedout|eai_again|getaddrinfo|offline/i.test(
    message,
  );
}

// The company is disabled or its subscription is paused —
// enforce_subscription_write_access_by_outlet() raises P0001 with this
// prefix. The write is perfectly valid and WILL succeed once the company is
// re-enabled, so it must never be discarded: the grace-period path would
// otherwise delete a real, paid order (and all its items/settlements) from
// the device after 30 minutes, which is exactly how Luma & Lua lost a day's
// bills on 2026-10-08. Like a connectivity failure, it is retried
// indefinitely and never counts against the grace period.
export const SUBSCRIPTION_PAUSED_EVENT_THROTTLE_MS = 5 * 60 * 1000;

export function isSubscriptionPausedError(message?: string | null): boolean {
  if (!message) return false;
  return /subscription_paused/i.test(message);
}

export function stripGeneratedColumns(
  table: string,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const cols = GENERATED_COLUMNS[table];
  if (!cols || cols.length === 0) return payload;
  const stripped = { ...payload };
  cols.forEach((col) => {
    delete stripped[col];
  });
  return stripped;
}

// Identifies "the same" transaction across repeated uploadData() invocations
// so its first-failure time can be tracked for the grace period above.
// transactionId is stable for all crud entries in an explicit transaction;
// falls back to the entry's own clientId for untransacted changes
// (transactionId is null in that case).
export function getRetryKey(
  transactionId: number | null | undefined,
  failedOp: { clientId: number | string } | null,
): string | number {
  if (transactionId != null) return transactionId;
  if (failedOp) return `client:${failedOp.clientId}`;
  return 'unknown';
}

// Outcome of attempting to resolve an orders-table conflict via RPC:
//   - 'resolved': the RPC succeeded and the local transaction can complete.
//   - 'retry': the RPC failed, or reported the conflict isn't resolvable yet
//     (e.g. no other open order holds the table) — bubble up for
//     PowerSync's normal retry loop, subject to the grace period below.
// Both resolve_duplicate_table_order and reassign_order_bill_number derive
// the outlet/table authoritatively from p_table_id rather than looking up
// the losing/conflicting order row (which, for the scenarios these RPCs
// exist to handle, never exists server-side — its own INSERT is what
// failed with 23505 in the first place). So neither RPC has a "the order
// doesn't exist" case to report anymore — there is no 'not-found' outcome.
export type ConflictResolutionOutcome = 'resolved' | 'retry';

// Bounded-retry check shared by every "reached the server but not yet
// resolved" path. Reads the first-failure timestamp for retryKey, seeds it
// if absent, and reports whether the grace period has elapsed — clearing
// the tracked timestamp only when it has. Callers must NOT call
// firstFailureAt.delete(retryKey) themselves before a 'retry' outcome —
// doing so resets the elapsed-time clock to zero on every single retry,
// which is what made the grace period unreachable for orders-conflict
// retries before this helper existed (see supabaseConnector.ts).
export function checkGracePeriod(
  firstFailureAt: Map<string | number, number>,
  retryKey: string | number,
): { expired: boolean; firstFailedAt: number } {
  const firstFailedAt = firstFailureAt.get(retryKey) ?? Date.now();
  firstFailureAt.set(retryKey, firstFailedAt);
  const expired = Date.now() - firstFailedAt >= UPLOAD_RETRY_GRACE_PERIOD_MS;
  if (expired) firstFailureAt.delete(retryKey);
  return { expired, firstFailedAt };
}

export type OrdersConflictKind = 'bill-number' | 'duplicate-table' | 'other';

// orders carries two independent UNIQUE constraints that both surface as a
// plain 23505 with no further structure from PowerSync's CrudEntry — the
// Postgres error message/details is the only place the actual constraint
// name shows up, so it must be inspected to tell them apart:
//   - orders_outlet_bill_unique (outlet_id, bill_number): a numbering
//     collision — fixable by reassigning the next available bill number,
//     the order itself is perfectly valid.
//   - idx_orders_single_open_per_table (table_id, WHERE status_id = 9): a
//     genuine double-booking — two devices opened an order for the same
//     table while both offline. Renumbering does nothing for this case.
export function classifyOrdersConflict(error: {
  message?: string | null;
  details?: string | null;
}): OrdersConflictKind {
  const text = `${error.message ?? ''} ${error.details ?? ''}`;
  if (/orders_outlet_bill_unique/i.test(text)) return 'bill-number';
  if (/idx_orders_single_open_per_table/i.test(text)) return 'duplicate-table';
  return 'other';
}

// Fields handleBillNumberConflict actually needs before calling
// reassign_order_bill_number. `table_id` is deliberately NOT required: it's
// legitimately null for every TAKEAWAY/CASH order (no table). A previous
// version of this check required it, which meant a bill-number collision on
// any non-DINE order could never call the RPC at all — it silently returned
// 'retry' forever, blocking that device's entire upload queue until the
// 30-minute grace period discarded the transaction outright. See
// supabase/migrations/20260908150000_fix_reassign_bill_number_for_takeaway_orders.sql
// for the matching server-side fix (reassign_order_bill_number resolving the
// outlet from p_outlet_id when there's no table).
export function hasRequiredBillConflictFields(
  opData: Record<string, unknown>,
): boolean {
  return Boolean(opData.mode) && Boolean(opData.waiter_id);
}
